import { useEffect, useRef } from 'react';
import io from 'socket.io-client';
import Toast from '../components/Shared/Toast/Toast';
import CommonConfig from './constant';
import { useNavigate } from 'react-router-dom';

const socketUrl = process.env.REACT_APP_API_ENDPOINT;

// Single shared connection for the whole app (multiple components use one socket).
let sharedSocket = null;
let sharedUserId = null;
let currentNavigate = null;
let intentionalDisconnect = false;
const buildCallbacks = new Set();
const notificationCallbacks = new Set();

export const disconnectSocket = () => {
  intentionalDisconnect = true;
  if (sharedSocket) {
    sharedSocket.removeAllListeners();
    sharedSocket.disconnect();
    sharedSocket = null;
    sharedUserId = null;
  }
};

const getSharedSocket = (userId) => {
  if (sharedSocket && sharedUserId === userId) return sharedSocket;

  if (sharedSocket) {
    sharedSocket.removeAllListeners();
    sharedSocket.disconnect();
    sharedSocket = null;
  }

  intentionalDisconnect = false;
  sharedUserId = userId;
  sharedSocket = io(socketUrl, {
    transports: ['websocket', 'polling'],
    auth: (cb) => cb({ token: localStorage.getItem('access_token'), userId }),
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
  });

  sharedSocket.on('connect_error', (error) => console.error('❌ Socket error:', error));
  sharedSocket.on('disconnect', (reason) => {
    console.warn('⚠️ Socket disconnected:', reason);
    if (intentionalDisconnect) return;
    if (reason === 'io server disconnect' && sharedSocket) {
      sharedSocket.connect();
    }
  });

  sharedSocket.on('logout', async (data) => {
    Toast.info({ message: data.message });
    try {
      const currentUser = CommonConfig.loginData('ERPLoginData');
      await CommonConfig.handleLogout(currentUser?._id, currentNavigate);
    } catch (error) {
      console.error('🚫 Logout failed:', error);
    }
  });

  sharedSocket.on('notification', (data) => {
    notificationCallbacks.forEach((cb) => cb && cb(data));
  });

  sharedSocket.on('newFeatureReleased', () => {
    buildCallbacks.forEach((cb) => cb && cb());
  });

  sharedSocket.on('accessUpdated', () => {
    Toast.info({ message: 'Your access has been updated.' });
    CommonConfig.removeCookie('allowedPaths');
    setTimeout(() => {
      window.location.href = window.location.origin;
    }, 800);
  });

  return sharedSocket;
};

const useSocket = (onNewBuildFound, OnNewNotification) => {
  const navigate = useNavigate();
  const buildRef = useRef(onNewBuildFound);
  const notifRef = useRef(OnNewNotification);
  buildRef.current = onNewBuildFound;
  notifRef.current = OnNewNotification;

  useEffect(() => {
    const currentUser = CommonConfig.loginData('ERPLoginData');
    const userId = currentUser?._id;

    if (!userId || !socketUrl) return;

    currentNavigate = navigate;
    getSharedSocket(userId);

    const buildCb = () => buildRef.current && buildRef.current();
    const notifCb = (data) => notifRef.current && notifRef.current(data);
    buildCallbacks.add(buildCb);
    notificationCallbacks.add(notifCb);

    return () => {
      buildCallbacks.delete(buildCb);
      notificationCallbacks.delete(notifCb);
    };
  }, [navigate]);

  return sharedSocket;
};

export default useSocket;
